import {
  Injectable,
  Renderer2,
  RendererFactory2,
  NgZone,
} from '@angular/core';
import { ActionMenuOption } from './action-menu.component';
import { BehaviorSubject } from 'rxjs';

export interface ActionMenuConfig {
  menuId: string;
  triggerElement: HTMLElement;
  options: ActionMenuOption[];
  position?: 'right' | 'left' | 'auto';
  offset?: { x: number; y: number };
  zIndex?: number;
  allowFlip?: boolean; // Whether to flip menu position when there's not enough space
}

@Injectable({
  providedIn: 'root',
})
export class ActionMenuService {
  private openMenus = new Set<string>();
  private renderer: Renderer2;
  private menuState = new Map<string, ActionMenuOption[]>();
  public menuState$ = new BehaviorSubject(this.menuState);

  constructor(
    rendererFactory: RendererFactory2,
    private zone: NgZone
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
    // Close menus when clicking outside
    document.addEventListener('click', (event) => {
      if (this.openMenus.size > 0) {
        const target = event.target as HTMLElement;
        if (!target.closest('.action-menu') && !target.closest('[opsActionMenu]')) {
          this.closeAllActionMenus();
        }
      }
    });

    // Close menus when pressing ESC
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.openMenus.size > 0) {
        this.closeAllActionMenus();
      }
    });

    // Close menus on scroll to avoid misalignment
    document.addEventListener(
      'scroll',
      () => {
        if (this.openMenus.size > 0) {
          this.closeAllActionMenus();
        }
      },
      true // capture phase to react early in the scroll lifecycle
    );
  }

  /**
   * Opens an action menu with dynamic positioning
   * @param config Configuration for the action menu
   */
  openActionMenu(config: ActionMenuConfig): void {
    const {
      menuId,
      triggerElement,
      options,
      position = 'right',
      offset = config.offset || { x: 0, y: 0 },
      zIndex = 1001,
      allowFlip = true,
    } = config;

    // Close all other action menus first
    this.closeAllActionMenus();

    // Position the menu dynamically
    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        const menuElement = document.getElementById(menuId);
        if (menuElement && triggerElement) {
          // make measurable
          this.renderer.setStyle(menuElement, 'display', 'block');
          this.renderer.setStyle(menuElement, 'visibility', 'hidden');
          this.renderer.setStyle(menuElement, 'position', 'fixed');
          this.renderer.setStyle(menuElement, 'top', '0');
          this.renderer.setStyle(menuElement, 'left', '0');

          const triggerRect = triggerElement.getBoundingClientRect();
          const scale = window.visualViewport ? window.visualViewport.scale || 1 : 1;

          let left: number;
          let top: number;

          // Get menu dimensions for smart positioning
          const { width: menuWidth, height: menuHeight } = menuElement.getBoundingClientRect();

          // Calculate horizontal position relative to viewport
          if (position === 'right') {
            left = triggerRect.right + offset.x;
          } else if (position === 'left') {
            left = triggerRect.left - menuWidth - offset.x;
          } else {
            // auto - center aligned with trigger
            const triggerCenterX = triggerRect.left + triggerRect.width / 2;
            const menuCenterX = menuWidth / 2;
            left = triggerCenterX - menuCenterX + offset.x;
          }

          // Calculate vertical position with flip logic relative to viewport
          if (allowFlip) {
            const spaceBelow = window.innerHeight - triggerRect.bottom;
            const spaceAbove = triggerRect.top;

            if (spaceBelow >= menuHeight + offset.y) {
              // Enough space below - position normally
              top = triggerRect.bottom  + offset.y ;
            } else if (spaceAbove >= menuHeight + offset.y) {
              // Not enough space below but enough above - flip to top
              top = triggerRect.top - menuHeight - offset.y;
            } else {
              // Not enough space in either direction - position below but adjust if needed
              top = triggerRect.bottom + offset.y;
            }
          } else {
            // No flipping - always position below
            top = triggerRect.bottom + offset.y;
          }
          // console.log(triggerRect.bottom, left * scale, top * scale);
          // Apply fixed positioning relative to viewport
          this.renderer.setStyle(menuElement, 'position', 'fixed');
          this.renderer.setStyle(menuElement, 'top', `${top * scale}px`);
          this.renderer.setStyle(menuElement, 'left', `${left * scale}px`);
          this.renderer.setStyle(menuElement, 'zIndex', zIndex.toString());
          this.renderer.setStyle(menuElement, 'transform', 'none'); // Override existing transform
          this.renderer.setStyle(menuElement, 'visibility', 'visible');

          // Ensure menu stays within viewport
          this.ensureMenuInViewport(menuElement);
        }
      });
    });

    // Show the menu
    this.showMenu(menuId);
    this.openMenus.add(menuId);
    this.menuState.set(menuId, options);
    this.menuState$.next(this.menuState);
  }

  /**
   * Closes a specific action menu
   * @param menuId ID of the menu to close
   */
  closeActionMenu(menuId: string): void {
    this.hideMenu(menuId);
    this.resetMenuPosition(menuId);
    this.openMenus.delete(menuId);
    this.menuState.delete(menuId);
    this.menuState$.next(this.menuState);
  }

  /**
   * Closes all open action menus
   */
  closeAllActionMenus(): void {
    this.openMenus.forEach((menuId) => {
      this.hideMenu(menuId);
      this.resetMenuPosition(menuId);
    });
    this.openMenus.clear();
    this.menuState.clear();
    this.menuState$.next(this.menuState);
  }

  /**
   * Toggles an action menu (opens if closed, closes if open)
   * @param config Configuration for the action menu
   */
  toggleActionMenu(config: ActionMenuConfig): void {
    const { menuId } = config;
    
    if (this.openMenus.has(menuId)) {
      this.closeActionMenu(menuId);
    } else {
      this.openActionMenu(config);
    }
  }

  private resetMenuPosition(menuId: string): void {
    const menuElement = document.getElementById(menuId);
    if (menuElement) {
      this.renderer.removeStyle(menuElement, 'position');
      this.renderer.removeStyle(menuElement, 'top');
      this.renderer.removeStyle(menuElement, 'left');
      this.renderer.removeStyle(menuElement, 'zIndex');
      this.renderer.removeStyle(menuElement, 'transform');
      this.renderer.removeStyle(menuElement, 'display');
      this.renderer.removeClass(menuElement, 'visible');
    }
  }

  private showMenu(menuId: string): void {
    const menuElement = document.getElementById(menuId);
    if (menuElement) {
      this.renderer.setStyle(menuElement, 'display', 'block');
      this.renderer.addClass(menuElement, 'visible');
    }
  }

  private hideMenu(menuId: string): void {
    const menuElement = document.getElementById(menuId);
    if (menuElement) {
      this.renderer.setStyle(menuElement, 'display', 'none');
      this.renderer.removeClass(menuElement, 'visible');
    }
  }

  private ensureMenuInViewport(menuElement: HTMLElement): void {
    const rect = menuElement.getBoundingClientRect();
    const margin = 5; // Small margin from viewport edge
    
    // Check if menu goes significantly beyond right edge
    if (rect.right > window.innerWidth - margin) {
      const newLeft = window.innerWidth - rect.width - margin;
      this.renderer.setStyle(menuElement, 'left', `${newLeft}px`);
    }
    
    // Check if menu goes significantly beyond bottom edge
    if (rect.bottom > window.innerHeight - margin) {
      const newTop = window.innerHeight - rect.height - margin;
      this.renderer.setStyle(menuElement, 'top', `${newTop}px`);
    }
    
    // Check if menu goes significantly beyond left edge
    if (rect.left < margin) {
      this.renderer.setStyle(menuElement, 'left', `${margin}px`);
    }
    
    // Check if menu goes significantly beyond top edge
    if (rect.top < margin) {
      this.renderer.setStyle(menuElement, 'top', `${margin}px`);
    }
  }
} 